import {
  DOCUMENT,
  Injectable,
  Renderer2,
  RendererFactory2,
  RendererStyleFlags2,
  inject,
} from '@angular/core';
import { CssAttributes } from '../../../features/calendar/components/time-block/rendering/css-attributes';

@Injectable({
  providedIn: 'root',
})
export class ShiveRenderService {
  private readonly renderer: Renderer2;
  private readonly rendererFactory = inject(RendererFactory2);
  private readonly document = inject(DOCUMENT);

  constructor() {
    this.renderer = this.rendererFactory.createRenderer(null, null);
  }

  public setStyle(
    natEl: unknown | HTMLElement,
    attrKey: string,
    attrVal: string,
    flags?: RendererStyleFlags2,
  ): void {
    this.renderer.setStyle(natEl, attrKey, attrVal, flags);
  }

  public removeStyle(natEl: unknown | HTMLElement, attrKey: string): void {
    this.renderer.removeStyle(natEl, attrKey);
  }

  public addClass(natEl: unknown | HTMLElement, cssClass: string): void {
    this.renderer.addClass(natEl, cssClass);
  }

  public removeClass(natEl: unknown | HTMLElement, cssClass: string): void {
    this.renderer.removeClass(natEl, cssClass);
  }

  public appendChild(parent: unknown, newChild: unknown): void {
    this.renderer.appendChild(parent, newChild);
  }

  public removeChild(parent: unknown, oldChild: unknown, isHostElement?: boolean): void {
    this.renderer.removeChild(parent, oldChild, isHostElement);
  }

  public setAttribute(el: any, name: string, value: string, namespace?: string | null): void {
    this.renderer.setAttribute(el, name, value, namespace);
  }

  public removeAttribute(el: any, name: string, namespace?: string | null): void {
    this.renderer.removeAttribute(el, name, namespace);
  }

  public createElement(name: string, namespace?: string | null): unknown {
    return this.renderer.createElement(name, namespace);
  }

  public createText(text: string): any {
    this.renderer.createText(text);
  }

  public setProperty(el: any, name: string, value: any): void {
    this.renderer.setProperty(el, name, value);
  }

  public listen(
    target: 'window' | 'document' | 'body' | any,
    eventName: string,
    callback: (event: any) => boolean | void,
  ): () => void {
    return this.renderer.listen(target, eventName, callback);
  }

  public insertBefore(
    parent: unknown,
    newChild: unknown,
    refChild: unknown,
    isMove?: boolean,
  ): void {
    this.renderer.insertBefore(parent, newChild, refChild, isMove);
  }

  public moveElement(natEl: unknown | HTMLElement, x = 0, y = 0): void {
    const transformProp =
      'transform' in document.body.style
        ? 'transform'
        : 'webkitTransform' in document.body.style
          ? 'webkitTransform'
          : 'mozTransform' in document.body.style
            ? 'mozTransform'
            : 'oTransform' in document.body.style
              ? 'oTransform'
              : 'msTransform' in document.body.style
                ? 'msTransform'
                : null;

    if (transformProp) {
      this.setStyle(natEl, transformProp, `translate3d(${x}px, ${y}px, 0)`);
    } else {
      // / Fallback
      this.setStyle(natEl, CssAttributes.left, `${x}px`);
      this.setStyle(natEl, CssAttributes.top, `${y}px`);
    }
  }

  public getAttribute(natEl: Element, attrKey: string): string | null {
    return natEl.getAttribute(attrKey);
  }

  public getCSSVar(name: string): string {
    return getComputedStyle(this.document.documentElement).getPropertyValue(name).trim();
  }

  public setCssVar(name: string, value: string | number): void {
    this.renderer.setStyle(
      this.document.documentElement,
      name,
      typeof value === 'number' ? `${value}px` : value,
      RendererStyleFlags2.DashCase,
    );
  }
}
